// Spaces, channels, topics, threads and messages, DMs, attachments, follows and the inbox
// (04 §Spaces and chat; 06). M2-01 contracts: handlers are M2-02 (chat), M2-04 (inbox,
// follows), M2-05 (attachments). Spaces, attachments, follows and the inbox are part of the
// base plan (ADR-0016 §8: "work and chat" are core; boards live in spaces and tasks have
// threads and attachments); channels, topics, messages and DMs are gated on `chat`. M2-02 adds
// channel subscriptions (join, leave, notification level, mute, follow), topic follow, message
// bookmarks and the agent wake-up of a mention.
import { z } from "zod";
import { AgentPrincipalKind, AgentReadinessState, AgentWake } from "../agent-rows";
import {
  ChannelKind,
  ChannelMemberRole,
  ChannelNotifyLevel,
  ChannelPostingPolicy,
  DigestFrequency,
  FollowLevel,
  FollowSubjectKind,
  NotificationCategory,
  NotificationOverrideLevel,
  NotificationPreset,
  ReplyLength,
  SearchHitKind,
  SpaceKind,
} from "../enums";
import { boundedRecord, FreeText, LIMITS, NonEmpty, Timestamp, text, Uuid } from "../primitives";
import {
  BlockNoteDoc,
  Board,
  Channel,
  ChannelMember,
  ColumnGateView,
  Follow,
  InboxItem,
  Message,
  MessageBookmark,
  Reaction,
  Space,
  SpaceSettings,
  Task,
  Thread,
  Topic,
  TopicMember,
} from "../work-rows";
import { CHAT_FILE_LIMITS, ChatAttachment } from "./chat-files";
import { CompanyItemParams, CompanyParams, PageQuery, page } from "./common";
import { MemoryScopeRef } from "./memory";
import { defineRoute } from "./route";
import {
  SPACE_SHARE_MAX,
  SpaceShareInput,
  SpaceShareList,
  SpaceShareListQuery,
  SpaceShareSet,
} from "./space-shares";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;
const chat = { feature: "chat" } as const;

// Spaces ----------------------------------------------------------------------------------------

export const SpaceCreate = z
  .strictObject({
    name: NonEmpty,
    kind: SpaceKind.exclude(["personal"]),
    team_id: Uuid.nullable().optional(),
    icon: z.string().max(64).nullable().optional(),
    description: FreeText.nullable().optional(),
    is_private: z.boolean().optional(),
    settings: SpaceSettings.optional(),
  })
  .meta({ id: "SpaceCreate" });
/** `personal` spaces are created with their owner's membership (M4) and never by this route. */
export const SpacePatch = SpaceCreate.omit({ kind: true }).partial().meta({ id: "SpacePatch" });

export const SpaceListQuery = PageQuery.extend({
  kind: SpaceKind.optional(),
  include_archived: z.enum(["true", "false"]).optional(),
});

export const SpaceMemberAdd = z
  .strictObject({ membership_id: Uuid, relation: z.enum(["viewer", "editor", "owner"]) })
  .meta({ id: "SpaceMemberAdd" });

/**
 * A channel row in a sidebar or the channel browser, with the caller's unread count and
 * subscription (M2-02): `joined` (a member row; in a public channel it grants nothing), its
 * notification level and mute, and whether the caller follows the channel.
 */
export const SidebarChannel = Channel.extend({
  unread_count: z.int().min(0),
  last_message_at: Timestamp.nullable(),
  joined: z.boolean(),
  notify_level: ChannelNotifyLevel.nullable(),
  muted: z.boolean(),
  following: z.boolean(),
  /**
   * WF-FIX (2026-10-26): the caller may add members here (`channelAccess(...).invite`: a private
   * channel's members, a public one's posters; never read-only), so pickers offer only these.
   */
  can_invite: z.boolean(),
  /**
   * WF-FIX (2026-10-26): the caller manages the channel (`channelAccess(...).manage`: the space's
   * owners and the channel's admins; never a DM or a read-only channel) — e.g. removes others.
   */
  can_manage: z.boolean(),
  /**
   * CHAT-PRESENCE (2026-10-29): the caller closed this DM (`dms.close`, `dms.cleanup`) and nobody
   * has written in it since; the sidebar leaves it out. Always false for a channel.
   */
  closed: z.boolean(),
}).meta({ id: "SidebarChannel" });

/**
 * `GET spaces/{id}/sidebar` (04): channels, boards and pages with unread counts in one call
 * (one query plus the permission filter; 04 §Performance budgets). `pages` stays empty until
 * M7-02 lands pages.
 */
export const SpaceSidebar = z
  .object({
    space: Space,
    channels: z.array(SidebarChannel),
    boards: z.array(Board),
    pages: z.array(z.object({ id: Uuid, title: z.string(), parent_page_id: Uuid.nullable() })),
    unread_total: z.int().min(0),
  })
  .meta({ id: "SpaceSidebar" });

// Channels and topics ---------------------------------------------------------------------------

export const ChannelCreate = z
  .strictObject({
    space_id: Uuid,
    kind: z.enum(["space", "agent_room"]).optional().describe("Default `space`"),
    name: NonEmpty,
    description: FreeText.nullable().optional(),
    is_private: z.boolean().optional(),
    member_ids: z.array(Uuid).max(LIMITS.list).optional(),
    /** M2-02: `admins` lets only the space's owners and the channel's admins post. */
    posting_policy: ChannelPostingPolicy.optional(),
    /** CHAT-CONTROLS: `admins` lets only the channel's managers open topics. */
    topic_policy: ChannelPostingPolicy.optional(),
    default_notify_level: ChannelNotifyLevel.optional(),
    /** MULTI-SPACE-ACCESS: more spaces it is shared into, each with the level its members get. */
    shares: z
      .array(SpaceShareInput)
      .max(SPACE_SHARE_MAX)
      .optional()
      .describe(
        "Also share with these spaces (spaces the caller may add to; not the home space; not with is_private)",
      ),
  })
  .meta({ id: "ChannelCreate" });
export const ChannelPatch = z
  .strictObject({
    name: NonEmpty.optional(),
    description: FreeText.nullable().optional(),
    is_private: z.boolean().optional(),
    is_agent_room: z.boolean().optional(),
    posting_policy: ChannelPostingPolicy.optional(),
    /** CHAT-CONTROLS: who may open topics, and the notification level new members start with. */
    topic_policy: ChannelPostingPolicy.optional(),
    /** CHAT-SWITCH-A: who may use `@channel` / `@here`. */
    broadcast_policy: ChannelPostingPolicy.optional(),
    default_notify_level: ChannelNotifyLevel.optional(),
  })
  .meta({ id: "ChannelPatch" });

/** CHAT-CONTROLS: a manager makes a member a channel admin, or an admin a member again. */
export const ChannelMemberPatch = z
  .strictObject({ role: ChannelMemberRole })
  .meta({ id: "ChannelMemberPatch" });

/** CHAT-CONTROLS: `DELETE channels/{id}` takes the channel's name typed back (Slack's confirm). */
export const ChannelDeleteQuery = z
  .object({ confirm: NonEmpty })
  .meta({ id: "ChannelDeleteQuery" });

/**
 * `GET channels` (M2-02 browse): every channel the caller can view; `joined=true` the ones it
 * belongs to (the sidebar), `joined=false` the ones it may join (browse public channels).
 */
export const ChannelListQuery = PageQuery.extend({
  space_id: Uuid.optional(),
  include_archived: z.enum(["true", "false"]).optional(),
  joined: z.enum(["true", "false"]).optional(),
  /**
   * WF-FIX (2026-10-26): only the channels this membership is a member of (a DE's channels on
   * its profile), still among the ones the caller can view.
   */
  member_id: Uuid.optional(),
});

export const ChannelMemberAdd = z
  .strictObject({ membership_id: Uuid, role: ChannelMemberRole.optional() })
  .meta({ id: "ChannelMemberAdd" });

export const ChannelRead = z.strictObject({ message_id: Uuid }).meta({ id: "ChannelRead" });

/** The caller's subscription to one channel (M2-02). `role` is null when not joined. */
export const ChannelSubscription = z
  .object({
    channel_id: Uuid,
    membership_id: Uuid,
    joined: z.boolean(),
    role: ChannelMemberRole.nullable(),
    notify_level: ChannelNotifyLevel.nullable(),
    muted: z.boolean(),
    following: z.boolean(),
    last_read_message_id: Uuid.nullable(),
  })
  .meta({ id: "ChannelSubscription" });

/** Notification level, mute and follow of a joined channel; at least one field. */
export const ChannelSubscriptionPatch = z
  .strictObject({
    notify_level: ChannelNotifyLevel.optional(),
    muted: z.boolean().optional(),
    following: z.boolean().optional(),
  })
  .refine((p) => Object.keys(p).length > 0, { message: "at least one field" })
  .meta({ id: "ChannelSubscriptionPatch" });

/**
 * CHAT-TOPICS-SEPARATE: where a topic sits. `is_main`: the channel's own conversation (its
 * `(no topic)` topic: messages posted to the channel, not to a topic; its members are the
 * channel's). `is_member`: the caller is a member of the topic (of the channel, for the main one).
 * `is_opener`: the caller opened the topic (and is still a member): it may make it private and
 * remove members.
 */
const topicPlaceFields = {
  is_main: z.boolean(),
  is_member: z.boolean(),
  is_opener: z.boolean(),
};

/**
 * A topic in the channel's topic list with the caller's unread count and follow. CHAT-TOPICS-
 * SEPARATE: unread counts only for members (0 otherwise); `member_count` is the topic's members
 * (the main topic: 0, its members are the channel's).
 */
export const TopicSummary = Topic.extend({
  thread_id: Uuid,
  unread_count: z.int().min(0),
  following: z.boolean(),
  ...topicPlaceFields,
  member_count: z.int().min(0),
}).meta({ id: "TopicSummary" });

/** CHAT-TOPICS-SEPARATE: a topic's members (the whole list; topics stay small). */
export const TopicMemberList = z
  .object({ data: z.array(TopicMember) })
  .meta({ id: "TopicMemberList" });

/**
 * CHAT-TOPICS-SEPARATE: add or remove topic members (at least one id). Joining an open topic is
 * adding yourself; leaving is removing yourself. Any member adds people who can see the channel;
 * a member removes only itself unless it opened the topic or manages the channel.
 */
export const TopicMembersPatch = z
  .strictObject({
    add: z.array(Uuid).max(50).optional(),
    remove: z.array(Uuid).max(50).optional(),
  })
  .refine((p) => (p.add?.length ?? 0) + (p.remove?.length ?? 0) > 0, {
    message: "add or remove at least one member",
  })
  .meta({ id: "TopicMembersPatch" });

/** CHAT-TOPICS-SEPARATE: make a topic private (members only) or open; its opener or a manager. */
export const TopicPrivacy = z.strictObject({ private: z.boolean() }).meta({ id: "TopicPrivacy" });

export const TopicSubscriptionPatch = z
  .strictObject({ following: z.boolean() })
  .meta({ id: "TopicSubscriptionPatch" });
export const TopicSubscription = z
  .object({ topic_id: Uuid, membership_id: Uuid, following: z.boolean() })
  .meta({ id: "TopicSubscription" });

export const TopicCreate = z.strictObject({ name: text(60) }).meta({ id: "TopicCreate" });
export const TopicRename = z.strictObject({ name: text(60) }).meta({ id: "TopicRename" });
export const TopicResolve = z.strictObject({ resolved: z.boolean() }).meta({ id: "TopicResolve" });
/** CHAT-UX: any poster pins; only the channel's managers unpin. */
export const TopicPin = z.strictObject({ pinned: z.boolean() }).meta({ id: "TopicPin" });
/** `GET channels/{id}/topics`: archived topics are left out unless a manager asks for them. */
export const TopicListQuery = PageQuery.extend({
  include_archived: z
    .enum(["true", "false"])
    .transform((v) => v === "true")
    .optional(),
});
export const TopicMove = z.strictObject({ channel_id: Uuid }).meta({ id: "TopicMove" });
export const TopicSplit = z
  .strictObject({ name: text(60), message_ids: z.array(Uuid).min(1).max(500) })
  .meta({ id: "TopicSplit" });

export const TopicDetail = Topic.extend({ thread_id: Uuid, ...topicPlaceFields }).meta({
  id: "TopicDetail",
});

// Messages --------------------------------------------------------------------------------------

/**
 * `GET threads/{id}/messages` (04): `before` / `after` are message cursors for scrolling both
 * ways; neither gives the newest page.
 */
export const MessageListQuery = z.object({
  before: z.string().max(1024).optional(),
  after: z.string().max(1024).optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});

const MentionRef = z
  .string()
  .max(80)
  .regex(/^(person|seat|team|de):[0-9a-f-]{36}$/);
const CardRef = z
  .string()
  .max(80)
  // GUIDED-FLOW: `flow:<run id>` is the "Complete your seat setup, 3 of 5 chapters" card.
  // DE-REPLY-STYLE: `task_link:<task id>` is a reply's link to its task (a small chip, no card).
  .regex(/^(task|task_link|approval|page|delegation_request|status_ask|flow):[0-9a-f-]{36}$/);

/**
 * `body` (BlockNote) or `body_markdown` (converted server-side, 04 §Base rules), exactly one.
 * `client_id` is the local-first id: `(company_id, client_id)` is idempotent forever (04).
 */
export const MessageCreate = z
  .strictObject({
    client_id: z.string().min(1).max(128),
    body: BlockNoteDoc.optional(),
    body_markdown: z
      .string()
      .min(1)
      .max(LIMITS.longText * 4)
      .optional(),
    parent_message_id: Uuid.nullable().optional(),
    mentions: z.array(MentionRef).max(LIMITS.list).optional(),
    card_refs: z.array(CardRef).max(20).optional(),
    /**
     * THREAD-WAKE: who a follow-up goes to, when the person changed it in the composer. An
     * agent's membership id addresses it (as an @mention does: it is woken and becomes the
     * conversation's addressee); `none` is a note that wakes no addressee. Absent: the server
     * picks the conversation's addressee (`threads.agents` `addressee_membership_id`).
     */
    addressee: z.union([Uuid, z.literal("none")]).optional(),
    /**
     * CHAT-FILES: the caller's staged files (`attachments.upload`, same thread) to send with the
     * message. With files, the text may be left out (a files-only message).
     */
    attachment_ids: z.array(Uuid).max(CHAT_FILE_LIMITS.maxPerMessage).optional(),
    /**
     * CHAT-SWITCH-A: `@channel` notifies every person in the channel, `@here` those of them
     * active now (Slack's broadcast). Only in a channel (never a DM), only a person, and only
     * when the channel's `broadcast_policy` lets the caller (403 otherwise).
     */
    broadcast: z.enum(["channel", "here"]).optional(),
    /**
     * CHAT-THREAD-AGENTS: "Also send to #channel" on a reply: the server posts a copy on the
     * channel's main line (quoting the root; a note that wakes no agent), and the agents the reply
     * wakes copy their answer there too. Their answer always goes in the thread. Only with
     * `parent_message_id`.
     */
    also_to_channel: z.boolean().optional(),
  })
  .refine((m) => !m.also_to_channel || !!m.parent_message_id, {
    message: "also_to_channel needs parent_message_id",
  })
  .refine(
    (m) =>
      (m.body === undefined) !== (m.body_markdown === undefined) ||
      (m.body === undefined && m.body_markdown === undefined && !!m.attachment_ids?.length),
    { message: "exactly one of body and body_markdown (or files only)" },
  )
  .meta({ id: "MessageCreate" });

export const MessagePatch = z
  .strictObject({
    body: BlockNoteDoc.optional(),
    body_markdown: z
      .string()
      .min(1)
      .max(LIMITS.longText * 4)
      .optional(),
    mentions: z.array(MentionRef).max(LIMITS.list).optional(),
  })
  .refine((m) => (m.body === undefined) !== (m.body_markdown === undefined), {
    message: "exactly one of body and body_markdown",
  })
  .meta({ id: "MessagePatch" });

export const MessageItemParams = z.object({ co: Uuid, id: Uuid, mid: Uuid });
/** CHAT-THREAD-AGENTS: one agent of the reply thread under `mid`. */
export const ThreadAgentParams = z.object({ co: Uuid, id: Uuid, mid: Uuid, membership_id: Uuid });
export const ReactionParams = z.object({
  co: Uuid,
  id: Uuid,
  mid: Uuid,
  emoji: z.string().min(1).max(64),
});

export const ReactionAdd = z
  .strictObject({ emoji: z.string().min(1).max(64) })
  .meta({ id: "ReactionAdd" });
export const MessagePin = z.strictObject({ pinned: z.boolean() }).meta({ id: "MessagePin" });

/**
 * AGENT-MENTION: what became of each agent the message woke or meant to wake (its mentions, a
 * `topic` channel's agents), in name order; empty when it asked no agent.
 */
const agentWakes = z.array(AgentWake);

/** A message with its reactions and whether the caller bookmarked it, as the thread renders it. */
export const MessageView = Message.extend({
  reactions: z.array(Reaction),
  bookmarked: z.boolean(),
  agent_wakes: agentWakes,
  /** CHAT-FILES: the message's files, in the order they were sent (none on a deleted one). */
  attachments: z.array(ChatAttachment),
  /** CHAT-SWITCH-B: posted by a channel's incoming webhook (shown as an Integration). */
  integration: z.object({ webhook_id: Uuid, name: z.string() }).nullable().optional(),
  /**
   * REPLY-COUNT: on a root that has live replies, how many there are in all and when the last
   * one came (the loaded page may hold only some of them). Absent on a reply or a root with none.
   */
  reply_count: z.int().min(1).optional(),
  last_reply_at: Timestamp.optional(),
}).meta({
  id: "MessageView",
});

/** `POST messages`: the row as stored, with its agents' wake results (AGENT-MENTION). */
export const MessagePosted = Message.extend({
  agent_wakes: agentWakes,
  attachments: z.array(ChatAttachment),
}).meta({
  id: "MessagePosted",
});

/**
 * AGENT-MENTION: an agent the caller may mention here, and whether a mention would wake it now
 * (the @ menu). `can_add`: the caller may add it to this channel (the channel's own add-member
 * rule, offered to the channel's managers and company admins), which is what `no_access` needs.
 */
export const AgentReadiness = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    agent_kind: AgentPrincipalKind,
    seat_title: z.string().nullable(),
    state: AgentReadinessState,
    reason: z.string(),
    can_add: z.boolean(),
    /**
     * CHAT-DE: whether the agent can read the thread now, whatever else stops a wake (paused,
     * a silent channel): "Just this once" is offered only to one that reads it.
     */
    can_read: z.boolean(),
    /** THREAD-WAKE: it took part in the conversation (posted there, or was mentioned there). */
    participating: z.boolean(),
  })
  .meta({ id: "AgentReadiness" });
export const ThreadAgentReadiness = z
  .object({
    /** The thread's channel (null: a task or approval thread, which has no members to add). */
    channel_id: Uuid.nullable(),
    channel_name: z.string().nullable(),
    data: z.array(AgentReadiness),
    /**
     * THREAD-WAKE: the agent a person's follow-up without an @mention wakes in the conversation
     * (`root`'s replies, or the topic's top level): the one that last replied there, or else the
     * one last addressed. Null: a follow-up wakes nobody (a DM, a `silent` or `topic` channel,
     * no agent there yet).
     */
    addressee_membership_id: Uuid.nullable(),
  })
  .meta({ id: "ThreadAgentReadiness" });
/** THREAD-WAKE: the conversation inside a thread: a root message's replies (absent: top level). */
export const ThreadAgentsQuery = z.object({ root: Uuid.optional() });

/** AGENT-MENTION: ask again for one agent the message named (after it was added, resumed…). */
export const MessageWake = z.strictObject({ membership_id: Uuid }).meta({ id: "MessageWake" });

/** M2-02: bookmark a message (or remove the bookmark with `false`); private to the caller. */
export const MessageBookmarkPut = z
  .strictObject({ bookmarked: z.boolean() })
  .meta({ id: "MessageBookmarkPut" });

/** One of the caller's bookmarks with the message as it is now and where it is. */
export const BookmarkView = z
  .object({
    bookmark: MessageBookmark,
    message: Message,
    channel_id: Uuid.nullable(),
    topic_id: Uuid.nullable(),
  })
  .meta({ id: "BookmarkView" });

/**
 * CHAT-CONTROLS (Slack's Threads view): a thread the caller is in — started it, replied in it,
 * or follows its topic — as `GET my/threads` lists it: the root as the caller reads it, where
 * it is, the replies' count, authors and last time. THREAD-READS: unread is per thread (the
 * replies by others after the caller's read position in this thread), not per channel.
 */
export const ThreadSummaryView = z
  .object({
    root: MessageView,
    channel_id: Uuid,
    channel_name: z.string(),
    channel_kind: ChannelKind,
    topic_id: Uuid,
    topic_name: z.string(),
    reply_count: z.int().min(0),
    last_reply_at: Timestamp,
    /** Reply authors in first-reply order (null: a system line), the root's author excluded. */
    participant_ids: z.array(Uuid.nullable()),
    /** THREAD-READS: `unread_count > 0`. */
    unread: z.boolean(),
    /** THREAD-READS: replies by others after the caller's read position in this thread. */
    unread_count: z.int().min(0),
    /** THREAD-READS: the caller follows the thread (always true in `my/threads`). */
    following: z.boolean(),
    last_read_at: Timestamp.nullable(),
  })
  .meta({ id: "ThreadSummaryView" });

/**
 * THREAD-READS: move the caller's read position in a reply thread (the root `mid`'s replies).
 * Without `reply_id`, to its newest reply. Positions never move backwards.
 */
export const ThreadReadBody = z
  .strictObject({ reply_id: Uuid.optional() })
  .meta({ id: "ThreadReadBody" });

/** THREAD-READS: follow or unfollow a reply thread by hand (Slack's "Follow thread"). */
export const ThreadFollowBody = z
  .strictObject({ following: z.boolean() })
  .meta({ id: "ThreadFollowBody" });

/** THREAD-READS: the caller's state in one reply thread after a read or a follow. */
export const ThreadReadState = z
  .object({
    root_message_id: Uuid,
    following: z.boolean(),
    unread_count: z.int().min(0),
    last_read_reply_id: Uuid.nullable(),
    last_read_at: Timestamp.nullable(),
  })
  .meta({ id: "ThreadReadState" });

/** THREAD-READS: "Mark all read" — how many threads it moved. */
export const ThreadsReadAll = z.object({ threads: z.int().min(0) }).meta({ id: "ThreadsReadAll" });

export const MessagePage = z
  .object({
    data: z.array(MessageView),
    next_cursor: z.string().nullable().describe("`before` cursor for older messages"),
    prev_cursor: z.string().nullable().describe("`after` cursor for newer messages"),
  })
  .meta({ id: "MessagePage" });

/** "Make this a task" (06): a task on `board_id` whose thread links back to the message. */
export const MakeTask = z
  .strictObject({
    board_id: Uuid,
    client_id: z.string().min(1).max(128),
    title: NonEmpty.optional().describe("Default: the message's first line"),
    assignee_seat_ids: z.array(Uuid).max(20).optional(),
    /** CREATE-TASK-FIELDS: the board's field values (by field id), as `POST tasks` takes them. */
    fields: boundedRecord(z.unknown(), LIMITS.list, Uuid).optional(),
  })
  .meta({ id: "MakeTask" });

export const DmOpen = z
  .strictObject({ membership_ids: z.array(Uuid).min(1).max(15) })
  .meta({ id: "DmOpen" });

// Follows, inbox (chat files: chat-files.ts) -----------------------------------------------------

export const FollowPut = z
  .strictObject({
    subject_kind: FollowSubjectKind,
    subject_id: Uuid,
    level: FollowLevel.optional(),
  })
  .meta({ id: "FollowPut" });
export const FollowDeleteQuery = z.object({ subject_kind: FollowSubjectKind, subject_id: Uuid });

/**
 * The inbox list's filters (06 §Inbox). Without one: every item not done and not snoozed. M2-04
 * adds `done` (done items, to undo) and `snoozed` (items snoozed into the future). UX pass 1.4
 * adds `memory` (live items of kind `memory`: what Recall proposes, the memory review's queue).
 */
export const InboxFilter = z.enum([
  // INBOX-PREFS: the tabs Waiting on me (approvals, questions a DE needs) and DMs.
  "waiting",
  "dms",
  "unread",
  "approvals",
  "mentions",
  "followed",
  "memory",
  "done",
  "snoozed",
]);
export const InboxQuery = PageQuery.extend({ filter: InboxFilter.optional() });

/**
 * INBOX-READABLE: a listed inbox item. A Recall proposal notice (kind `memory`, subject
 * `recall_notice`) carries the memory scope it is about, with its name, read on list, so the
 * inbox groups notices per scope and links the memory review filtered to it. Absent on others.
 */
export const InboxListItem = InboxItem.extend({
  /**
   * BOARD-GATES: a task row whose card waits in a person-only column carries that gate as the
   * reader sees it (one batched lookup per page). Absent: no gate, or column gates are off.
   */
  review_gate: ColumnGateView.optional(),
  memory_scope: MemoryScopeRef.extend({
    /** A seat a digital employee holds now (the inbox labels it "DE", not "seat"). */
    de: z.boolean().optional(),
  })
    .nullable()
    .optional(),
}).meta({ id: "InboxListItem" });
export type InboxListItem = z.infer<typeof InboxListItem>;
export const InboxRead = z.strictObject({ read: z.boolean().optional() }).meta({ id: "InboxRead" });
export const InboxDone = z.strictObject({ done: z.boolean().optional() }).meta({ id: "InboxDone" });
export const InboxReadAll = z
  .strictObject({ filter: InboxFilter.optional() })
  .meta({ id: "InboxReadAll" });
export const InboxReadAllResult = z
  .object({ count: z.int().min(0) })
  .meta({ id: "InboxReadAllResult" });
/** M2-04: snooze an item until a time (out of the list until then); `null` wakes it now. */
export const InboxSnooze = z
  .strictObject({ until: Timestamp.nullable() })
  .meta({ id: "InboxSnooze" });

// Search (M2-04) ----------------------------------------------------------------------------------
// 06 §Shell "quick-switcher search": messages and tasks the caller can read, matched with
// Postgres full-text search (prefix words), newest first. Messages need the `chat` add-on;
// without it only tasks are searched.

/** CHAT-SWITCH-A: `has:` on the search page (`file`: messages with a file). */
export const SearchHas = z.enum(["file"]);
/** CHAT-SWITCH-A: `is:` on the search page (`thread`: thread roots with replies, and replies). */
export const SearchIs = z.enum(["thread"]);

/**
 * `q` (prefix words), and CHAT-SWITCH-A's filters (Slack's `from:` `in:` `has:file` `before:`
 * `after:` `is:thread`): any filter limits the search to messages. `before` / `after` are dates
 * (`YYYY-MM-DD`, UTC): `before` excludes that day, `after` starts the day after (Slack's
 * meaning). `q` may be left out when a filter is given.
 */
export const SearchQuery = PageQuery.extend({
  q: z.string().trim().min(1).max(200).optional(),
  kind: SearchHitKind.optional(),
  /** Messages written by this member. */
  from: Uuid.optional(),
  /** Messages of this channel (or DM). */
  in: Uuid.optional(),
  has: SearchHas.optional(),
  is: SearchIs.optional(),
  before: z.iso.date().optional(),
  after: z.iso.date().optional(),
}).refine(
  (s) =>
    s.q !== undefined ||
    s.from !== undefined ||
    s.in !== undefined ||
    s.has !== undefined ||
    s.is !== undefined ||
    s.before !== undefined ||
    s.after !== undefined,
  { message: "a search needs words or a filter", path: ["q"] },
);

/** One hit: a message (with where it is) or a task (with its board and number). */
export const SearchHit = z
  .object({
    kind: SearchHitKind,
    id: Uuid,
    /** A task's title, or the message's `#channel › topic`. */
    title: z.string(),
    /** The matched text (a message's `body_text`, a task's title), at most 200 characters. */
    snippet: z.string(),
    created_at: Timestamp,
    board_id: Uuid.nullable(),
    task_number: z.int().min(1).nullable(),
    thread_id: Uuid.nullable(),
    channel_id: Uuid.nullable(),
    topic_id: Uuid.nullable(),
    author_membership_id: Uuid.nullable(),
    /** CHAT-SWITCH-A: a reply's thread root (opens the thread panel); null otherwise. */
    parent_message_id: Uuid.nullable().optional(),
  })
  .meta({ id: "SearchHit" });

// ⌘K palette (SEARCH-NOTIF; 06 §Shell "jump", extending M2-04's search) -------------------------
// One call answers the palette: the company's nouns the caller may see whose name matches —
// people, digital employees, seats, teams, metrics, this quarter's priorities, boards, docs,
// channels — plus M2-04's tasks and messages, a few of each, every section filtered by the rule of
// its own list route. The client's recent picks (`recent`, newest first) are resolved under the
// same rules and come back first, flagged `recent`. Setup pages and commands are the client's.

export const PaletteHitKind = z
  .enum([
    "person",
    "agent",
    "seat",
    "team",
    "metric",
    "priority",
    "board",
    "doc",
    "channel",
    "task",
    "message",
    // TASK-FILES: a task file by name (opens its task).
    "file",
  ])
  .meta({ id: "PaletteHitKind" });

export const PaletteQuery = z.object({
  /** Empty or absent: the recent items only. */
  q: z.string().trim().max(200).optional(),
  /** The client's recent picks, newest first: `kind:id` joined by commas (at most 20). */
  recent: z
    .string()
    .max(2000)
    .regex(/^([a-z]+:[0-9a-f-]{36})(,[a-z]+:[0-9a-f-]{36}){0,19}$/)
    .optional(),
  /** Hits per kind (default 5). */
  per_kind: z.coerce.number().int().min(1).max(20).optional(),
});

export const PaletteHit = z
  .object({
    kind: PaletteHitKind,
    id: Uuid,
    title: z.string(),
    /** What it is, in a few words ("Digital employee · Recruiter", "Metric · weekly"). */
    subtitle: z.string().nullable(),
    /** One of the caller's recent picks. */
    recent: z.boolean(),
    /** Where it opens: a seat (person, agent, seat, priority), a board (board, task), a chat place. */
    seat_id: Uuid.nullable(),
    board_id: Uuid.nullable(),
    task_number: z.int().min(1).nullable(),
    channel_id: Uuid.nullable(),
    topic_id: Uuid.nullable(),
  })
  .meta({ id: "PaletteHit" });

export const PaletteResult = z
  .object({
    /** Recent hits first (in the client's order), then by kind in `PaletteHitKind` order. */
    data: z.array(PaletteHit),
    /** Server time spent, in milliseconds (the palette's budget is p95 < 150 ms). */
    took_ms: z.number().min(0),
  })
  .meta({ id: "PaletteResult" });

// Notification preferences (SEARCH-NOTIF; 06 §Notification preferences) -------------------------
// The caller's own, in this company: per kind of inbox item, which channels carry it (the inbox,
// the email digest, a desktop notification), quiet hours and the digest's frequency. Resolved
// over the defaults; a PUT changes only what it names. Channel mutes and levels stay
// `channels.setPrefs`'.

export const NotificationKind = z
  .enum([
    "mention",
    "reply",
    "assignment",
    "task_change",
    "approval",
    "agent_result",
    "kpi_due",
    "budget_alert",
  ])
  .meta({ id: "NotificationKind" });

export const NotificationChannels = z
  .strictObject({ inbox: z.boolean(), email: z.boolean(), desktop: z.boolean() })
  .meta({ id: "NotificationChannels" });

const TimeOfDay = z
  .string()
  .max(5)
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const QuietHours = z
  .strictObject({
    /** Local "HH:MM"; a window that ends before it starts runs past midnight. */
    start: TimeOfDay,
    end: TimeOfDay,
    /** IANA zone, e.g. "Europe/Bucharest". */
    time_zone: z.string().min(1).max(64),
  })
  .meta({ id: "QuietHours" });

/** INBOX-PREFS: where one notification type goes. `email`: only once an email channel exists. */
export const NotificationRoute = z
  .strictObject({
    inbox: z.boolean(),
    alert: z.boolean(),
    email: z.boolean(),
    digest: z.boolean(),
  })
  .meta({ id: "NotificationRoute" });

export const NotificationRoutePatch = z
  .strictObject({
    inbox: z.boolean().optional(),
    alert: z.boolean().optional(),
    email: z.boolean().optional(),
    digest: z.boolean().optional(),
  })
  .meta({ id: "NotificationRoutePatch" });

/** INBOX-PREFS: a per-item override (a task or a DE; channels keep their own level). */
export const NotificationOverride = z
  .strictObject({
    subject_kind: z.enum(["task", "agent"]),
    subject_id: Uuid,
    level: NotificationOverrideLevel,
    /**
     * What the settings list shows: "#12 Ship the offer" for a task the caller can still see, the
     * DE's name; null when it is gone (or no longer visible to the caller).
     */
    subject_name: z.string().nullable(),
  })
  .meta({ id: "NotificationOverride" });

export const NotificationPrefs = z
  .object({
    /** SEARCH-NOTIF's per-kind view of the rows below (email = digest, desktop = alert). */
    types: z.record(NotificationKind, NotificationChannels),
    /**
     * INBOX-PREFS: the person's preset (null: following the company's default, `company_preset`)
     * and the one in force.
     */
    preset: NotificationPreset.nullable(),
    effective_preset: NotificationPreset,
    company_preset: NotificationPreset,
    /** INBOX-PREFS: every notification type's destinations, resolved (the settings grid). */
    routes: z.record(NotificationCategory, NotificationRoute),
    /** INBOX-PREFS: whether any row differs from the preset (the picker shows "Custom"). */
    customized: z.boolean(),
    /** INBOX-PREFS: false while no per-item email channel exists (the column shows disabled). */
    email_available: z.boolean(),
    /** INBOX-PREFS: alerts and the digest hold until then (null: not paused). */
    paused_until: Timestamp.nullable(),
    /** INBOX-PREFS: the hours a waiting item waits before its one reminder (company setting). */
    nudge_hours: z.int().min(1).max(168),
    overrides: z.array(NotificationOverride).max(500),
    quiet_hours: QuietHours.nullable(),
    digest_frequency: DigestFrequency,
    /** When the last digest email went out (null: never). */
    last_digest_at: Timestamp.nullable(),
    /** Null: every default (nothing changed yet). */
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "NotificationPrefs" });

/** DE-REPLY-STYLE: how long the caller wants DEs' chat replies in this company. */
export const ReplyStyle = z
  .object({
    length: ReplyLength,
    /** When it was last chosen (null: never; the default, short, applies). */
    updated_at: Timestamp.nullable(),
    /**
     * DE-QUICK-ACK: DEs send an instant first reply ("On it — …", or a quick question) before
     * their main run (on by default).
     */
    quick_replies: z.boolean(),
    /** CHAT-COST-PILLS: the cost label on agents' chat messages (on by default). */
    show_costs: z.boolean(),
  })
  .meta({ id: "ReplyStyle" });

export const ReplyStylePut = z
  .strictObject({
    length: ReplyLength.optional(),
    quick_replies: z.boolean().optional(),
    show_costs: z.boolean().optional(),
  })
  .refine(
    (b) => b.length !== undefined || b.quick_replies !== undefined || b.show_costs !== undefined,
    { message: "send length, quick_replies or show_costs" },
  )
  .meta({ id: "ReplyStylePut" });

export const NotificationPrefsPut = z
  .strictObject({
    types: z
      .partialRecord(
        NotificationKind,
        z.strictObject({
          inbox: z.boolean().optional(),
          email: z.boolean().optional(),
          desktop: z.boolean().optional(),
        }),
      )
      // Keys are the eight kinds (bounds.test: every request record is bounded).
      .meta({ maxProperties: 8 })
      .optional(),
    quiet_hours: QuietHours.nullable().optional(),
    digest_frequency: DigestFrequency.optional(),
    /**
     * INBOX-PREFS: start from a preset (null: the company's default). Choosing one clears the
     * row edits; `routes` in the same request then apply over it.
     */
    preset: NotificationPreset.nullable().optional(),
    /** INBOX-PREFS: row edits over the preset (nine rows at most). */
    routes: z
      .partialRecord(NotificationCategory, NotificationRoutePatch)
      .meta({ maxProperties: 9 })
      .optional(),
    /**
     * INBOX-PREFS: pause alerts and the digest for an hour, until the end of today, or until
     * Monday (local time in `time_zone`, else the quiet hours' zone, else UTC); null resumes.
     */
    pause: z
      .strictObject({
        for: z.enum(["1h", "today", "monday"]),
        time_zone: z.string().min(1).max(64).optional(),
      })
      .nullable()
      .optional(),
    /** INBOX-PREFS: set (a level) or clear (null) per-item overrides on tasks and DEs. */
    overrides: z
      .array(
        z.strictObject({
          subject_kind: z.enum(["task", "agent"]),
          subject_id: Uuid,
          level: NotificationOverrideLevel.nullable(),
        }),
      )
      .max(50)
      .optional(),
  })
  .meta({ id: "NotificationPrefsPut" });

// Per-person chat settings, agent rules, bookmarks (M2-06 additions) --------------------------
// What the Chat screens need beyond M2-01's routes: the caller's own state per channel (joined,
// muted, notification level), a channel's agent wake rule and standing instructions (agents-in-
// chat research 2026-10-20, recommendations 3 and 17), saved messages (06 §Messages "bookmark")
// and the caller's follows. Handlers are M2-02/M2-04; the web fake twin serves them meanwhile.

/** A channel's notification level for one person: `none` = no channel follow (06 §Inbox). */
export const ChatNotifyLevel = z.enum(["all", "mentions", "none"]);

/**
 * The caller's state in one channel. `joined`: listed under "your channels" (public channels)
 * or a member (private ones); `muted`: no unread badge, nothing in the inbox but direct
 * mentions; `notify`: what reaches the inbox.
 */
export const ChannelPrefs = z
  .object({
    channel_id: Uuid,
    joined: z.boolean(),
    muted: z.boolean(),
    notify: ChatNotifyLevel,
    updated_at: Timestamp,
  })
  .meta({ id: "ChannelPrefs" });

export const ChannelPrefsPut = z
  .strictObject({
    joined: z.boolean().optional(),
    muted: z.boolean().optional(),
    notify: ChatNotifyLevel.optional(),
  })
  .meta({ id: "ChannelPrefsPut" });

/**
 * When agents in a channel wake (research rec. 3): `mentions` (default) — an @mention, an
 * assignment or a reply in a thread the agent started; `topic` — every message in a topic the
 * agent is in; `silent` — only on schedule. Several messages while a run is in flight join that
 * agent's next turn (one run per agent per topic; rec. 4).
 */
export const AgentWakeRule = z.enum(["mentions", "topic", "silent"]);

/**
 * DE-INITIATE: what DEs may start in the channel unprompted — `allowed` (the company's rules
 * apply), `routines` (declared routines only) or `off`. It can only narrow the company's rules.
 * A `silent` channel takes declared routines only, whatever this says.
 */
export const AgentInitiateRule = z.enum(["allowed", "routines", "off"]);

/** A channel's rules for agents, visible to its members (rec. 17: standing instructions). */
export const ChannelAgentRules = z
  .object({
    channel_id: Uuid,
    wake: AgentWakeRule,
    initiate: AgentInitiateRule,
    instructions: z.string().nullable(),
    updated_at: Timestamp.nullable(),
  })
  .meta({ id: "ChannelAgentRules" });

export const ChannelAgentRulesPut = z
  .strictObject({
    wake: AgentWakeRule,
    /** DE-INITIATE: absent keeps the stored rule. */
    initiate: AgentInitiateRule.optional(),
    instructions: FreeText.nullable().optional(),
  })
  .meta({ id: "ChannelAgentRulesPut" });

// Saved messages are M2-02's MessageBookmarkPut / BookmarkView (above).

export const FollowListQuery = PageQuery.extend({ subject_kind: FollowSubjectKind.optional() });

// Routes ----------------------------------------------------------------------------------------

export const chatRoutes = {
  "spaces.list": defineRoute({
    ...core,
    id: "spaces.list",
    method: "get",
    path: `${base}/spaces`,
    tag: "spaces",
    summary: "Spaces the caller can view",
    access: "member",
    params: CompanyParams,
    query: SpaceListQuery,
    response: { status: 200, schema: page(Space) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "spaces.create": defineRoute({
    ...core,
    id: "spaces.create",
    method: "post",
    path: `${base}/spaces`,
    tag: "spaces",
    summary: "Create a team, project or client space (with its default channel)",
    access: "member",
    params: CompanyParams,
    body: SpaceCreate,
    idempotency: "optional",
    response: { status: 201, schema: Space },
    errors: [],
    // CHAT-UX: a public space's channel is a default one, joined by the company's people.
    emits: ["space.created", "channel.created", "channel.member_changed", "access.granted"],
  }),
  "spaces.get": defineRoute({
    ...core,
    id: "spaces.get",
    method: "get",
    path: `${base}/spaces/{id}`,
    tag: "spaces",
    summary: "Get a space",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Space },
    errors: [],
    emits: [],
  }),
  "spaces.update": defineRoute({
    ...core,
    id: "spaces.update",
    method: "patch",
    path: `${base}/spaces/{id}`,
    tag: "spaces",
    summary: "Rename, re-team or change the settings of a space",
    access: "member",
    params: CompanyItemParams,
    body: SpacePatch,
    response: { status: 200, schema: Space },
    errors: [],
    emits: ["space.updated"],
  }),
  "spaces.archive": defineRoute({
    ...core,
    id: "spaces.archive",
    method: "post",
    path: `${base}/spaces/{id}/archive`,
    tag: "spaces",
    summary: "Archive a space with its channels and boards (nothing is deleted)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Space },
    errors: ["conflict"],
    emits: ["space.archived"],
  }),
  // ACTIONS: back from the archive with exactly what its archive took (same instant).
  "spaces.unarchive": defineRoute({
    ...core,
    id: "spaces.unarchive",
    method: "post",
    path: `${base}/spaces/{id}/unarchive`,
    tag: "spaces",
    summary: "Restore an archived space with the channels and boards its archive took",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Space },
    errors: ["conflict"],
    emits: ["space.unarchived"],
  }),
  "spaces.sidebar": defineRoute({
    ...core,
    id: "spaces.sidebar",
    method: "get",
    path: `${base}/spaces/{id}/sidebar`,
    tag: "spaces",
    summary: "Channels, boards, pages and unread counts in one call",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: SpaceSidebar },
    errors: [],
    emits: [],
  }),
  "spaces.addMember": defineRoute({
    ...core,
    id: "spaces.addMember",
    method: "post",
    path: `${base}/spaces/{id}/members`,
    tag: "spaces",
    summary: "Grant a member a relation on a non-team space (`space#viewer|editor|owner`)",
    access: "member",
    params: CompanyItemParams,
    body: SpaceMemberAdd,
    idempotency: "optional",
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["access.granted"],
  }),
  "spaces.removeMember": defineRoute({
    ...core,
    id: "spaces.removeMember",
    method: "delete",
    path: `${base}/spaces/{id}/members/{membership_id}`,
    tag: "spaces",
    summary: "Revoke a member's relations on a space (their follows inside it are pruned)",
    access: "member",
    params: z.object({ co: Uuid, id: Uuid, membership_id: Uuid }),
    response: { status: 204 },
    errors: [],
    emits: ["access.revoked", "follow.changed"],
  }),

  "channels.list": defineRoute({
    ...chat,
    id: "channels.list",
    method: "get",
    path: `${base}/channels`,
    tag: "channels",
    summary: "Channels the caller can view (optionally of one space)",
    access: "member",
    params: CompanyParams,
    query: ChannelListQuery,
    response: { status: 200, schema: page(SidebarChannel) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "channels.create": defineRoute({
    ...chat,
    id: "channels.create",
    method: "post",
    path: `${base}/channels`,
    tag: "channels",
    summary: "Create a channel in a space",
    access: "member",
    params: CompanyParams,
    body: ChannelCreate,
    idempotency: "optional",
    response: { status: 201, schema: Channel },
    errors: ["conflict"],
    emits: ["channel.created", "channel.member_changed", "channel.shares_changed"],
  }),
  "channels.shares.list": defineRoute({
    ...chat,
    id: "channels.shares.list",
    method: "get",
    path: `${base}/channel-shares`,
    tag: "channels",
    summary: "Channels shared into more spaces (MULTI-SPACE-ACCESS): the sidebar's Also-in rows",
    description:
      "Every share the caller can see: of a channel it can view, into a space it can view. `object_id` narrows it to one channel.",
    access: "member",
    params: CompanyParams,
    query: SpaceShareListQuery,
    response: { status: 200, schema: SpaceShareList },
    errors: [],
    emits: [],
  }),
  "channels.shares.set": defineRoute({
    ...chat,
    id: "channels.shares.set",
    method: "put",
    path: `${base}/channels/{id}/shares`,
    tag: "channels",
    summary: "Share a channel into more spaces, each with a level (the channel's managers)",
    description:
      "The whole set among the spaces the caller may add channels to: a share left out is removed; shares into other spaces stay. Not the home space, a personal space or an archived one, and never a private channel or a DM (422). Members of a shared space get the level on the channel: `owner` manages it, `editor` posts, `viewer` reads.",
    access: "member",
    params: CompanyItemParams,
    body: SpaceShareSet,
    response: { status: 200, schema: SpaceShareList },
    errors: ["conflict"],
    emits: ["channel.shares_changed"],
  }),
  "channels.get": defineRoute({
    ...chat,
    id: "channels.get",
    method: "get",
    path: `${base}/channels/{id}`,
    tag: "channels",
    summary: "Get a channel",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Channel },
    errors: [],
    emits: [],
  }),
  "channels.update": defineRoute({
    ...chat,
    id: "channels.update",
    method: "patch",
    path: `${base}/channels/{id}`,
    tag: "channels",
    summary: "Rename a channel or change its privacy / agent-room flag",
    access: "member",
    params: CompanyItemParams,
    body: ChannelPatch,
    response: { status: 200, schema: Channel },
    errors: [],
    emits: ["channel.updated"],
  }),
  "channels.archive": defineRoute({
    ...chat,
    id: "channels.archive",
    method: "post",
    path: `${base}/channels/{id}/archive`,
    tag: "channels",
    summary: "Archive a channel",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Channel },
    errors: ["conflict"],
    emits: ["channel.archived"],
  }),
  "channels.unarchive": defineRoute({
    ...chat,
    id: "channels.unarchive",
    method: "post",
    path: `${base}/channels/{id}/unarchive`,
    tag: "channels",
    summary: "Restore an archived channel (its managers; CHAT-CONTROLS)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Channel },
    errors: ["conflict"],
    emits: ["channel.restored"],
  }),
  "channels.delete": defineRoute({
    ...chat,
    id: "channels.delete",
    method: "delete",
    path: `${base}/channels/{id}`,
    tag: "channels",
    summary:
      "Delete a channel (its managers; `confirm` is its name typed back): a tombstone, never served again; messages kept. Default channels, board channels and DMs cannot be deleted",
    access: "member",
    params: CompanyItemParams,
    query: ChannelDeleteQuery,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["channel.deleted"],
  }),
  "channels.topics": defineRoute({
    ...chat,
    id: "channels.topics",
    method: "get",
    path: `${base}/channels/{id}/topics`,
    tag: "channels",
    summary: "Topics of a channel, newest activity first, with unread counts",
    access: "member",
    params: CompanyItemParams,
    query: TopicListQuery,
    response: { status: 200, schema: page(TopicSummary) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "channels.createTopic": defineRoute({
    ...chat,
    id: "channels.createTopic",
    method: "post",
    path: `${base}/channels/{id}/topics`,
    tag: "channels",
    summary: "Open a topic (and its thread); an existing name returns that topic",
    access: "member",
    params: CompanyItemParams,
    body: TopicCreate,
    idempotency: "optional",
    response: { status: 201, schema: TopicDetail },
    errors: [],
    emits: ["topic.created"],
  }),
  "channels.members": defineRoute({
    ...chat,
    id: "channels.members",
    method: "get",
    path: `${base}/channels/{id}/members`,
    tag: "channels",
    summary: "Members of a private channel or DM",
    access: "member",
    params: CompanyItemParams,
    query: PageQuery,
    response: { status: 200, schema: page(ChannelMember) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "channels.addMember": defineRoute({
    ...chat,
    id: "channels.addMember",
    method: "post",
    path: `${base}/channels/{id}/members`,
    tag: "channels",
    summary: "Add a member to a private channel",
    access: "member",
    params: CompanyItemParams,
    body: ChannelMemberAdd,
    idempotency: "optional",
    response: { status: 201, schema: ChannelMember },
    errors: ["conflict"],
    emits: ["channel.member_changed"],
  }),
  "channels.updateMember": defineRoute({
    ...chat,
    id: "channels.updateMember",
    method: "patch",
    path: `${base}/channels/{id}/members/{membership_id}`,
    tag: "channels",
    summary: "Make a member a channel admin, or an admin a member (its managers; CHAT-CONTROLS)",
    access: "member",
    params: z.object({ co: Uuid, id: Uuid, membership_id: Uuid }),
    body: ChannelMemberPatch,
    response: { status: 200, schema: ChannelMember },
    errors: [],
    emits: ["channel.member_changed"],
  }),
  "channels.removeMember": defineRoute({
    ...chat,
    id: "channels.removeMember",
    method: "delete",
    path: `${base}/channels/{id}/members/{membership_id}`,
    tag: "channels",
    summary: "Remove a member from a private channel",
    access: "member",
    params: z.object({ co: Uuid, id: Uuid, membership_id: Uuid }),
    response: { status: 204 },
    errors: [],
    emits: ["channel.member_changed", "follow.changed"],
  }),
  "channels.join": defineRoute({
    ...chat,
    id: "channels.join",
    method: "post",
    path: `${base}/channels/{id}/join`,
    tag: "channels",
    summary: "Join a public channel the caller can view (a subscription; it grants nothing)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: ChannelSubscription },
    errors: ["conflict"],
    emits: ["channel.member_changed"],
  }),
  "channels.leave": defineRoute({
    ...chat,
    id: "channels.leave",
    method: "post",
    path: `${base}/channels/{id}/leave`,
    tag: "channels",
    summary: "Leave a channel (a private channel's last member cannot; DMs cannot be left)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["channel.member_changed", "follow.changed"],
  }),
  "channels.subscription": defineRoute({
    ...chat,
    id: "channels.subscription",
    method: "patch",
    path: `${base}/channels/{id}/subscription`,
    tag: "channels",
    summary: "Set the caller's notification level, mute or follow on a joined channel",
    access: "member",
    params: CompanyItemParams,
    body: ChannelSubscriptionPatch,
    response: { status: 200, schema: ChannelSubscription },
    errors: ["conflict"],
    emits: ["channel.member_changed", "follow.changed"],
  }),
  "channels.takeOver": defineRoute({
    ...chat,
    id: "channels.takeOver",
    method: "post",
    path: `${base}/channels/{id}/take-over`,
    tag: "channels",
    summary: "Take over an agent room (agent runs targeting it park)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Channel },
    errors: ["conflict"],
    emits: ["agent_room.taken_over", "run.parked", "run.cancelled", "budget.released"],
  }),
  "channels.release": defineRoute({
    ...chat,
    id: "channels.release",
    method: "post",
    path: `${base}/channels/{id}/release`,
    tag: "channels",
    summary: "End a take-over (parked runs resume; the turn holder is woken)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: Channel },
    errors: ["conflict"],
    emits: ["agent_room.released", "run.unparked", "agent_room.turn_changed", "wakeup.requested"],
  }),
  "channels.read": defineRoute({
    ...chat,
    id: "channels.read",
    method: "post",
    path: `${base}/channels/{id}/read`,
    tag: "channels",
    summary: "Move the caller's read position (never backwards)",
    access: "member",
    params: CompanyItemParams,
    body: ChannelRead,
    response: { status: 204 },
    errors: [],
    emits: ["channel.read"],
  }),

  "topics.get": defineRoute({
    ...chat,
    id: "topics.get",
    method: "get",
    path: `${base}/topics/{id}`,
    tag: "topics",
    summary: "Get a topic with its thread id",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TopicDetail },
    errors: [],
    emits: [],
  }),
  "topics.rename": defineRoute({
    ...chat,
    id: "topics.rename",
    method: "post",
    path: `${base}/topics/{id}/rename`,
    tag: "topics",
    summary: "Rename a topic (an existing name merges into it)",
    access: "member",
    params: CompanyItemParams,
    body: TopicRename,
    response: { status: 200, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["topic.renamed"],
  }),
  "topics.resolve": defineRoute({
    ...chat,
    id: "topics.resolve",
    method: "post",
    path: `${base}/topics/{id}/resolve`,
    tag: "topics",
    summary: "Resolve (✓) or reopen a topic",
    access: "member",
    params: CompanyItemParams,
    body: TopicResolve,
    response: { status: 200, schema: TopicDetail },
    errors: [],
    emits: ["topic.resolved"],
  }),
  "topics.pin": defineRoute({
    ...chat,
    id: "topics.pin",
    method: "post",
    path: `${base}/topics/{id}/pin`,
    tag: "topics",
    summary: "Pin a topic to the top of its channel (any poster), or unpin it (managers)",
    access: "member",
    params: CompanyItemParams,
    body: TopicPin,
    response: { status: 200, schema: TopicDetail },
    errors: [],
    emits: ["topic.pinned"],
  }),
  "topics.archive": defineRoute({
    ...chat,
    id: "topics.archive",
    method: "post",
    path: `${base}/topics/{id}/archive`,
    tag: "topics",
    summary: "Archive a topic (hidden from the lists, messages kept); the channel's managers",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TopicDetail },
    errors: [],
    emits: ["topic.archived"],
  }),
  "topics.restore": defineRoute({
    ...chat,
    id: "topics.restore",
    method: "post",
    path: `${base}/topics/{id}/restore`,
    tag: "topics",
    summary: "Restore an archived topic; the channel's managers",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TopicDetail },
    errors: [],
    emits: ["topic.archived"],
  }),
  "topics.delete": defineRoute({
    ...chat,
    id: "topics.delete",
    method: "delete",
    path: `${base}/topics/{id}`,
    tag: "topics",
    summary: "Delete a topic: its messages become tombstones and it leaves every list; managers",
    access: "member",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["topic.deleted"],
  }),
  "topics.move": defineRoute({
    ...chat,
    id: "topics.move",
    method: "post",
    path: `${base}/topics/{id}/move`,
    tag: "topics",
    summary: "Move a topic to another channel",
    access: "member",
    params: CompanyItemParams,
    body: TopicMove,
    response: { status: 200, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["topic.moved"],
  }),
  "topics.split": defineRoute({
    ...chat,
    id: "topics.split",
    method: "post",
    path: `${base}/topics/{id}/split`,
    tag: "topics",
    summary: "Move selected messages into a new topic of the same channel",
    access: "member",
    params: CompanyItemParams,
    body: TopicSplit,
    idempotency: "optional",
    response: { status: 201, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["topic.split", "topic.created"],
  }),
  "topics.subscription": defineRoute({
    ...chat,
    id: "topics.subscription",
    method: "patch",
    path: `${base}/topics/{id}/subscription`,
    tag: "topics",
    summary: "Follow or unfollow a topic (following never grants visibility)",
    access: "member",
    params: CompanyItemParams,
    body: TopicSubscriptionPatch,
    response: { status: 200, schema: TopicSubscription },
    errors: [],
    emits: ["follow.changed"],
  }),
  "topics.members": defineRoute({
    ...chat,
    id: "topics.members",
    method: "get",
    path: `${base}/topics/{id}/members`,
    tag: "topics",
    summary:
      "Members of a topic (CHAT-TOPICS-SEPARATE): who is notified of it; a private topic's are the only ones who see it",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: TopicMemberList },
    errors: [],
    emits: [],
  }),
  "topics.updateMembers": defineRoute({
    ...chat,
    id: "topics.updateMembers",
    method: "post",
    path: `${base}/topics/{id}/members`,
    tag: "topics",
    summary:
      "Add or remove topic members (CHAT-TOPICS-SEPARATE): join an open topic by adding yourself, leave by removing yourself",
    access: "member",
    params: CompanyItemParams,
    body: TopicMembersPatch,
    response: { status: 200, schema: TopicMemberList },
    errors: ["conflict"],
    emits: ["topic.member_changed"],
  }),
  "topics.privacy": defineRoute({
    ...chat,
    id: "topics.privacy",
    method: "post",
    path: `${base}/topics/{id}/privacy`,
    tag: "topics",
    summary:
      "Make a topic private (only its members see it) or open (CHAT-TOPICS-SEPARATE): the topic's opener or the channel's managers",
    access: "member",
    params: CompanyItemParams,
    body: TopicPrivacy,
    response: { status: 200, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["topic.privacy_changed", "topic.member_changed"],
  }),
  "topics.takeOver": defineRoute({
    ...chat,
    id: "topics.takeOver",
    method: "post",
    path: `${base}/topics/{id}/take-over`,
    tag: "topics",
    summary: "Take over an agent-room topic",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["agent_room.taken_over", "run.parked", "run.cancelled", "budget.released"],
  }),
  "topics.release": defineRoute({
    ...chat,
    id: "topics.release",
    method: "post",
    path: `${base}/topics/{id}/release`,
    tag: "topics",
    summary: "End a topic take-over (parked runs resume; the turn holder is woken)",
    access: "member",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: TopicDetail },
    errors: ["conflict"],
    emits: ["agent_room.released", "run.unparked", "agent_room.turn_changed", "wakeup.requested"],
  }),

  "threads.get": defineRoute({
    ...chat,
    id: "threads.get",
    method: "get",
    path: `${base}/threads/{id}`,
    tag: "messages",
    summary: "Get a thread (its subject decides who may see it)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Thread },
    errors: [],
    emits: [],
  }),
  "threads.mine": defineRoute({
    ...chat,
    id: "threads.mine",
    method: "get",
    path: `${base}/my/threads`,
    tag: "messages",
    summary:
      "The threads the caller follows (started, replied in, mentioned in, followed by hand or in a followed topic; not unfollowed), latest reply first, with unread counts (CHAT-CONTROLS, THREAD-READS)",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(ThreadSummaryView) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  // THREAD-READS: Slack's per-thread read state and follows.
  "threads.readAll": defineRoute({
    ...chat,
    id: "threads.readAll",
    method: "post",
    path: `${base}/my/threads/read`,
    tag: "messages",
    summary: "Mark every thread the caller follows read (THREAD-READS)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ThreadsReadAll },
    errors: [],
    emits: ["thread.read"],
  }),
  "threads.read": defineRoute({
    ...chat,
    id: "threads.read",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/read`,
    tag: "messages",
    summary:
      "Move the caller's read position in the reply thread under `mid` (never backwards; THREAD-READS)",
    access: "member",
    params: MessageItemParams,
    body: ThreadReadBody,
    response: { status: 200, schema: ThreadReadState },
    errors: [],
    emits: ["thread.read"],
  }),
  "threads.follow": defineRoute({
    ...chat,
    id: "threads.follow",
    method: "put",
    path: `${base}/threads/{id}/messages/{mid}/follow`,
    tag: "messages",
    summary: "Follow or unfollow the reply thread under `mid` (THREAD-READS)",
    access: "member",
    params: MessageItemParams,
    body: ThreadFollowBody,
    response: { status: 200, schema: ThreadReadState },
    errors: [],
    emits: ["thread.follow_changed"],
  }),
  "messages.list": defineRoute({
    ...chat,
    id: "messages.list",
    method: "get",
    path: `${base}/threads/{id}/messages`,
    tag: "messages",
    summary: "Messages of a thread with a bidirectional cursor (`before` / `after`)",
    access: "member",
    params: CompanyItemParams,
    query: MessageListQuery,
    response: { status: 200, schema: MessagePage },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "messages.create": defineRoute({
    ...chat,
    id: "messages.create",
    method: "post",
    path: `${base}/threads/{id}/messages`,
    tag: "messages",
    summary: "Post a message (idempotent on `client_id` forever)",
    access: "member",
    params: CompanyItemParams,
    body: MessageCreate,
    idempotency: "optional",
    response: { status: 201, schema: MessagePosted },
    // M3-03: the thread gate's verdict precondition (ADR-0019 §2) on a held agent post.
    errors: [
      "conflict",
      "payload_too_large",
      "verdict_missing",
      "verdict_not_verified",
      "verdict_inconclusive",
    ],
    // M2-02: a mention of an agent that may read the thread wakes it (M3-02 run admission).
    // M3-03: an agent post its seat holds for approval raises the approval (the thread gate).
    // AGENT-MENTION: a mentioned agent that cannot be woken gets a dropped wake-up with why.
    emits: [
      "message.created",
      "wakeup.requested",
      "wakeup.dropped",
      "approval.requested",
      "agent_room.turn_changed",
      "attachment.attached",
    ],
  }),
  "messages.wake": defineRoute({
    ...chat,
    id: "messages.wake",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/wake`,
    tag: "messages",
    summary:
      "Ask again for an agent the message named that was not woken (after it was added to the channel, resumed or went live)",
    access: "member",
    params: MessageItemParams,
    body: MessageWake,
    response: { status: 200, schema: AgentWake },
    errors: [],
    emits: ["wakeup.requested", "wakeup.dropped"],
  }),
  "threads.agents": defineRoute({
    ...chat,
    id: "threads.agents",
    method: "get",
    path: `${base}/threads/{id}/agents`,
    tag: "messages",
    summary: "The agents the caller may mention in a thread, and whether a mention would wake each",
    access: "member",
    params: CompanyItemParams,
    query: ThreadAgentsQuery,
    response: { status: 200, schema: ThreadAgentReadiness },
    errors: [],
    emits: [],
  }),
  "threads.leaveAgent": defineRoute({
    ...chat,
    id: "threads.leaveAgent",
    method: "delete",
    path: `${base}/threads/{id}/messages/{mid}/agents/{membership_id}`,
    tag: "messages",
    summary:
      "Mute an agent in the reply thread under `mid`: replies naming nobody no longer wake it (CHAT-THREAD-AGENTS)",
    access: "member",
    params: ThreadAgentParams,
    response: { status: 204 },
    errors: [],
    emits: ["thread.agent_left"],
  }),
  "messages.update": defineRoute({
    ...chat,
    id: "messages.update",
    method: "patch",
    path: `${base}/threads/{id}/messages/{mid}`,
    tag: "messages",
    summary: "Edit one's own message inside the edit window",
    access: "member",
    params: MessageItemParams,
    body: MessagePatch,
    response: { status: 200, schema: Message },
    errors: ["edit_window_closed"],
    emits: ["message.edited"],
  }),
  "messages.delete": defineRoute({
    ...chat,
    id: "messages.delete",
    method: "delete",
    path: `${base}/threads/{id}/messages/{mid}`,
    tag: "messages",
    summary: "Delete a message (tombstone; its files go with it)",
    access: "member",
    params: MessageItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["message.deleted", "attachment.removed"],
  }),
  "messages.react": defineRoute({
    ...chat,
    id: "messages.react",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/reactions`,
    tag: "messages",
    summary: "Add a reaction",
    access: "member",
    params: MessageItemParams,
    body: ReactionAdd,
    response: { status: 201, schema: Reaction },
    errors: ["conflict"],
    emits: ["reaction.added"],
  }),
  "messages.unreact": defineRoute({
    ...chat,
    id: "messages.unreact",
    method: "delete",
    path: `${base}/threads/{id}/messages/{mid}/reactions/{emoji}`,
    tag: "messages",
    summary: "Remove one's reaction",
    access: "member",
    params: ReactionParams,
    response: { status: 204 },
    errors: [],
    emits: ["reaction.removed"],
  }),
  "messages.pin": defineRoute({
    ...chat,
    id: "messages.pin",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/pin`,
    tag: "messages",
    summary: "Pin or unpin a message",
    access: "member",
    params: MessageItemParams,
    body: MessagePin,
    response: { status: 200, schema: Message },
    errors: [],
    emits: ["message.pin_changed"],
  }),
  "messages.bookmark": defineRoute({
    ...chat,
    id: "messages.bookmark",
    method: "put",
    path: `${base}/threads/{id}/messages/{mid}/bookmark`,
    tag: "messages",
    summary: "Bookmark a message or remove the bookmark (private to the caller)",
    access: "member",
    params: MessageItemParams,
    body: MessageBookmarkPut,
    response: { status: 204 },
    errors: [],
    emits: ["message.bookmark_changed"],
  }),
  "bookmarks.list": defineRoute({
    ...chat,
    id: "bookmarks.list",
    method: "get",
    path: `${base}/bookmarks`,
    tag: "messages",
    summary: "The caller's bookmarks, newest first (messages it can no longer read are left out)",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(BookmarkView) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "messages.makeTask": defineRoute({
    ...chat,
    id: "messages.makeTask",
    method: "post",
    path: `${base}/threads/{id}/messages/{mid}/make-task`,
    tag: "messages",
    summary: "Make this message a task (the task's thread links back; the message shows a chip)",
    access: "member",
    params: MessageItemParams,
    body: MakeTask,
    idempotency: "optional",
    response: { status: 201, schema: Task },
    errors: ["conflict"],
    // M7-02: the message's body becomes the task's page.
    emits: ["task.created", "page.created", "task.file_changed", "attachment.saved"],
  }),
  "dms.open": defineRoute({
    ...chat,
    id: "dms.open",
    method: "post",
    path: `${base}/dms`,
    tag: "channels",
    summary: "Open the DM / group DM of a member set (the caller included); idempotent on the set",
    access: "member",
    params: CompanyParams,
    body: DmOpen,
    response: { status: 200, schema: Channel },
    // REACH-S1: an agent outside whose Hears from the caller is (the problem names whom to ask)
    errors: ["not_in_reach"],
    // CHAT-PRESENCE: opening a DM the caller closed lists it again.
    emits: ["channel.created", "topic.created", "channel.dm_reopened"],
  }),

  "follows.put": defineRoute({
    ...core,
    id: "follows.put",
    method: "put",
    path: `${base}/follows`,
    tag: "follows",
    summary: "Follow a subject the caller can view (never grants visibility)",
    access: "member",
    params: CompanyParams,
    body: FollowPut,
    response: { status: 200, schema: Follow },
    errors: [],
    emits: ["follow.changed"],
  }),
  "follows.delete": defineRoute({
    ...core,
    id: "follows.delete",
    method: "delete",
    path: `${base}/follows`,
    tag: "follows",
    summary: "Unfollow a subject",
    access: "member",
    params: CompanyParams,
    query: FollowDeleteQuery,
    response: { status: 204 },
    errors: [],
    emits: ["follow.changed"],
  }),

  "inbox.list": defineRoute({
    ...core,
    id: "inbox.list",
    method: "get",
    path: `${base}/inbox`,
    tag: "inbox",
    summary: "The caller's inbox (filters: unread, approvals, mentions, followed)",
    access: "member",
    params: CompanyParams,
    query: InboxQuery,
    response: { status: 200, schema: page(InboxListItem) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "inbox.read": defineRoute({
    ...core,
    id: "inbox.read",
    method: "post",
    path: `${base}/inbox/{id}/read`,
    tag: "inbox",
    summary: "Mark an item read (or unread with `read: false`)",
    access: "member",
    params: CompanyItemParams,
    body: InboxRead,
    response: { status: 200, schema: InboxItem },
    errors: [],
    emits: ["inbox_item.read"],
  }),
  "inbox.done": defineRoute({
    ...core,
    id: "inbox.done",
    method: "post",
    path: `${base}/inbox/{id}/done`,
    tag: "inbox",
    summary: "Mark an item done (or undone with `done: false`)",
    access: "member",
    params: CompanyItemParams,
    body: InboxDone,
    response: { status: 200, schema: InboxItem },
    errors: [],
    emits: ["inbox_item.done"],
  }),
  "inbox.readAll": defineRoute({
    ...core,
    id: "inbox.readAll",
    method: "post",
    path: `${base}/inbox/read-all`,
    tag: "inbox",
    summary: "Mark every unread item (of a filter) read",
    access: "member",
    params: CompanyParams,
    body: InboxReadAll,
    response: { status: 200, schema: InboxReadAllResult },
    errors: [],
    emits: ["inbox_item.read_all"],
  }),
  "inbox.snooze": defineRoute({
    ...core,
    id: "inbox.snooze",
    method: "post",
    path: `${base}/inbox/{id}/snooze`,
    tag: "inbox",
    summary: "Snooze an inbox item until a time, or wake it now (M2-04)",
    access: "member",
    params: CompanyItemParams,
    body: InboxSnooze,
    response: { status: 200, schema: InboxItem },
    errors: [],
    emits: ["inbox_item.snoozed"],
  }),
  "search.query": defineRoute({
    ...core,
    id: "search.query",
    method: "get",
    path: `${base}/search`,
    tag: "search",
    summary: "Full-text search over the messages and tasks the caller can read (M2-04)",
    access: "member",
    params: CompanyParams,
    query: SearchQuery,
    response: { status: 200, schema: page(SearchHit) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "search.palette": defineRoute({
    ...core,
    id: "search.palette",
    method: "get",
    path: `${base}/search/palette`,
    tag: "search",
    summary:
      "⌘K: people, digital employees, seats, teams, metrics, priorities, boards, docs, channels, tasks and messages the caller may see, recent picks first (SEARCH-NOTIF)",
    access: "member",
    params: CompanyParams,
    query: PaletteQuery,
    response: { status: 200, schema: PaletteResult },
    errors: [],
    emits: [],
  }),
  "notifications.prefs": defineRoute({
    ...core,
    id: "notifications.prefs",
    method: "get",
    path: `${base}/my/notification-prefs`,
    tag: "inbox",
    summary:
      "The caller's notification preferences in this company, over the defaults (SEARCH-NOTIF)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: NotificationPrefs },
    errors: [],
    emits: [],
  }),
  "notifications.setPrefs": defineRoute({
    ...core,
    id: "notifications.setPrefs",
    method: "put",
    path: `${base}/my/notification-prefs`,
    tag: "inbox",
    summary:
      "Change the caller's notification preferences: channels per kind, quiet hours, digest frequency (SEARCH-NOTIF)",
    access: "member",
    params: CompanyParams,
    body: NotificationPrefsPut,
    response: { status: 200, schema: NotificationPrefs },
    errors: [],
    emits: ["notification_prefs.changed"],
  }),
  "replyStyle.get": defineRoute({
    ...core,
    id: "replyStyle.get",
    method: "get",
    path: `${base}/my/reply-style`,
    tag: "inbox",
    summary: "How long the caller wants DEs' chat replies: short (default), normal or detailed",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ReplyStyle },
    errors: [],
    emits: [],
  }),
  "replyStyle.set": defineRoute({
    ...core,
    id: "replyStyle.set",
    method: "put",
    path: `${base}/my/reply-style`,
    tag: "inbox",
    summary: "Choose how long DEs' chat replies to the caller are (DE-REPLY-STYLE)",
    access: "member",
    params: CompanyParams,
    body: ReplyStylePut,
    response: { status: 200, schema: ReplyStyle },
    errors: [],
    emits: ["reply_style.changed"],
  }),
  // W1-FIXES: a DE records the reply length a person asked for in chat (the managed runtime's
  // `set_reply_style`; MCP `set_reply_style` does the same). The message is the person's own, in
  // a thread the agent may read; the length is set for its author and nobody else.
  "replyStyle.setFromMessage": defineRoute({
    ...chat,
    id: "replyStyle.setFromMessage",
    method: "put",
    path: `${base}/threads/{id}/messages/{mid}/reply-style`,
    tag: "messages",
    summary:
      "An agent records the reply length the person who wrote this message asked for (DE-REPLY-STYLE)",
    access: "member",
    params: MessageItemParams,
    body: ReplyStylePut,
    response: { status: 200, schema: ReplyStyle },
    errors: [],
    emits: ["reply_style.changed"],
  }),

  // M2-06 additions (see the schemas above).
  "channels.myPrefs": defineRoute({
    ...chat,
    id: "channels.myPrefs",
    method: "get",
    path: `${base}/my/channel-prefs`,
    tag: "channels",
    summary: "The caller's joined, muted and notification state of every channel they have set",
    access: "member",
    params: CompanyParams,
    query: PageQuery,
    response: { status: 200, schema: page(ChannelPrefs) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
  "channels.setPrefs": defineRoute({
    ...chat,
    id: "channels.setPrefs",
    method: "put",
    path: `${base}/channels/{id}/prefs`,
    tag: "channels",
    summary: "Join or leave a channel, mute it, or set its notification level (the caller's own)",
    access: "member",
    params: CompanyItemParams,
    body: ChannelPrefsPut,
    response: { status: 200, schema: ChannelPrefs },
    errors: ["conflict"],
    emits: ["channel.prefs_changed", "channel.member_changed", "follow.changed"],
  }),
  "channels.agentRules": defineRoute({
    ...chat,
    id: "channels.agentRules",
    method: "get",
    path: `${base}/channels/{id}/agent-rules`,
    tag: "channels",
    summary: "When agents in the channel wake, and the channel's standing instructions",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: ChannelAgentRules },
    errors: [],
    emits: [],
  }),
  "channels.setAgentRules": defineRoute({
    ...chat,
    id: "channels.setAgentRules",
    method: "put",
    path: `${base}/channels/{id}/agent-rules`,
    tag: "channels",
    summary: "Set the channel's agent wake rule and standing instructions",
    access: "member",
    params: CompanyItemParams,
    body: ChannelAgentRulesPut,
    response: { status: 200, schema: ChannelAgentRules },
    errors: [],
    emits: ["channel.agent_rules_changed"],
  }),
  // messages.bookmark and bookmarks.list are M2-02's (above).
  "follows.mine": defineRoute({
    ...core,
    id: "follows.mine",
    method: "get",
    path: `${base}/my/follows`,
    tag: "follows",
    summary: "What the caller follows (optionally of one subject kind)",
    access: "member",
    params: CompanyParams,
    query: FollowListQuery,
    response: { status: 200, schema: page(Follow) },
    errors: ["invalid_cursor"],
    emits: [],
  }),
} as const;
